import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import Card from '../../../../components/card/card.component';
import classes from './styles.module.scss';
import { useShared } from '../../../../contexts/shared.context';
import { useNavigate, useParams } from 'react-router-dom';
import { ICardName } from '../../../../types/kato.types';
import { useKato } from '../../../../contexts/kato.context';

const translationKey = 'cards.population-dynamic-card';

const PopulationDynamicCard = () => {
  const { t } = useTranslation();
  const { setFullscreen, selectedInfoYear } = useShared();
  const navigate = useNavigate();
  const { kato, link, tab } = useParams<{ kato: string, link: string, tab: string }>();
  const { populationIncreaseDynamicStats } = useKato().katoGeneralInfo;

  const onClick = () => {
    setFullscreen({ open: true, type: ICardName.POPULATION_DYNAMIC });
    navigate(`/${kato}/${link}/${tab}/${ICardName.POPULATION_DYNAMIC}`);
  };

  if (!populationIncreaseDynamicStats?.length) return null;

  const currentYear = selectedInfoYear || new Date().getFullYear();

  const rawData = populationIncreaseDynamicStats
    .filter(({ value }) => value !== null)
    .sort((a, b) => a.year - b.year);

  if (!rawData.length) return null;

  const scaleNeeded = +(rawData.find(d => d.year === currentYear)?.value ?? 0) > 1000;
  const scale = (v: number) => scaleNeeded ? +(v / 1000).toFixed(2) : v;

  const data = rawData.map(({ year, value }) => ({
    year,
    value: scale(+value),
  }));

  const subtitle = t(`${translationKey}.subtitle`, {
    type: scaleNeeded ? t('thousand') : '',
  });

  const renderTick = ({ x, y, payload }: any) => {
    const year = payload.value;
    const isCurrent = year === currentYear;
    return (
      <text
        x={x}
        y={20}
        textAnchor="middle"
        fill={isCurrent ? 'var(--color-primary)' : '#818689'}
        fontSize="0.75rem"
        fontWeight={isCurrent ? 700 : 400}
        key={year}
      >
        {year}
      </text>
    );
  };

  const renderDot = ({ cx, cy, payload }: any) => {
    const { year } = payload;
    const r = year === currentYear ? 6.5 : 5;
    const fill =
      year < currentYear
        ? '#92D050'
        : year === currentYear
          ? '#118F84'
          : '#6968FD';
    return <circle cx={cx} cy={cy} r={r} fill={fill} key={year} />;
  };

  return (
    <Card
      title={t(`${translationKey}.title`)}
      subtitle={subtitle}
      onClick={onClick}
    >
      <div className={classes['population-dynamic-card']}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 50, right: 20, left: 20, bottom: 0 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="year"
              axisLine={false}
              tickLine={false}
              tick={renderTick}
            />
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Tooltip
              cursor={false}
              formatter={(value: number) => [value, '']}
              labelFormatter={(label: number) => `${label}`}
              contentStyle={{
                fontSize: '0.75rem',
                maxWidth: '300px',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                color: '#202224',
              }}
            />
            <Line
              type="natural"
              dataKey="value"
              stroke="#ccc"
              strokeWidth={1}
              dot={renderDot}
              isAnimationActive={false}
              connectNulls
            >
              <LabelList
                dataKey="value"
                position="top"
                dy={-10}
                style={{
                  fontSize: '0.75rem',
                  fill: 'var(--color-primary)',
                  fontWeight: 700,
                }}
              />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default PopulationDynamicCard;
